/**
 * 함우리몰 판매 방식 — 개당/박스, 사진 단위, 배송 부과.
 * 가격·수량은 이 단위를 따른다. 박스를 옵션 추가금으로 넣지 않는다.
 */

export type MallSellUnit = 'EACH' | 'BOX'
export type MallImageShows = 'EACH' | 'BOX'
export type MallShippingCharge = 'ONCE' | 'PER_UNIT' | 'PER_N'

export interface MallSellPolicy {
  sellUnit?: MallSellUnit | string | null
  imageShows?: MallImageShows | string | null
  packQty?: number | null
  shippingCharge?: MallShippingCharge | string | null
  shippingEvery?: number | null
  shippingFee?: number | null
  minOrder?: number | null
  orderStep?: number | null
}

export function normalizeSellUnit(value: unknown): MallSellUnit {
  return String(value || '').toUpperCase() === 'BOX' ? 'BOX' : 'EACH'
}

export function normalizeImageShows(value: unknown): MallImageShows {
  return String(value || '').toUpperCase() === 'BOX' ? 'BOX' : 'EACH'
}

export function normalizeShippingCharge(value: unknown): MallShippingCharge {
  const raw = String(value || '').toUpperCase()
  if (raw === 'PER_UNIT') return 'PER_UNIT'
  if (raw === 'PER_N') return 'PER_N'
  return 'ONCE'
}

export function normalizePackQty(value: unknown): number {
  return Math.max(1, Math.trunc(Number(value) || 1))
}

/** 옵션 이름 앞의 구매 개수. "1개", "10개 (1카톤)" → 1, 10. 색상처럼 개수가 아니면 null. */
export function optionPieceCount(name: string): number | null {
  const matched = String(name || '').trim().match(/^(\d+)\s*개/)
  if (!matched) return null
  const qty = Number(matched[1])
  return qty > 0 ? qty : null
}

export function normalizeShippingEvery(value: unknown): number {
  return Math.max(1, Math.trunc(Number(value) || 2))
}

export function sellUnitNoun(unit: unknown): '개' | '박스' {
  return normalizeSellUnit(unit) === 'BOX' ? '박스' : '개'
}

export function sellUnitPriceLabel(unit: unknown): string {
  return normalizeSellUnit(unit) === 'BOX' ? '1박스' : '1개'
}

export function needsPackQty(policy: Pick<MallSellPolicy, 'sellUnit' | 'imageShows'>): boolean {
  return normalizeSellUnit(policy.sellUnit) === 'BOX'
}

export function imageShowsCaption(policy: MallSellPolicy): string {
  const sell = normalizeSellUnit(policy.sellUnit)
  const pack = normalizePackQty(policy.packQty)
  if (sell === 'BOX') {
    return pack > 1 ? `1박스(${pack}개) 구성` : '1박스 단위 판매'
  }
  return ''
}

export function sellUnitSummary(policy: MallSellPolicy): string {
  const pack = normalizePackQty(policy.packQty)
  if (normalizeSellUnit(policy.sellUnit) === 'BOX') {
    return pack > 1 ? `1박스(${pack}개)` : '1박스'
  }
  return '1개'
}

export function formatStockCount(qty: number, unit: unknown): string {
  const n = Math.max(0, Number(qty) || 0)
  return `${n.toLocaleString('ko-KR')}${sellUnitNoun(unit)}`
}

export function formatMinOrderLabel(minOrder: number, unit: unknown): string {
  const min = Math.max(1, Number(minOrder) || 1)
  return `최소 ${min.toLocaleString('ko-KR')}${sellUnitNoun(unit)}`
}

export function normalizeOrderStep(value: unknown): number {
  return Math.max(1, Math.trunc(Number(value) || 1))
}

export function snapOrderQty(
  qty: number,
  minOrder: number,
  orderStep: number,
  maxOrder?: number,
): number {
  const min = Math.max(1, Math.trunc(Number(minOrder) || 1))
  const step = normalizeOrderStep(orderStep)
  const cap = Math.max(min, Math.trunc(Number(maxOrder) || min))
  let n = Math.trunc(Number(qty) || min)
  if (!Number.isFinite(n)) n = min
  if (n < min) n = min
  n = Math.max(min, min + Math.round((n - min) / step) * step)
  while (n > cap && n - step >= min) {
    n -= step
  }
  if (n > cap) n = min
  return n
}

export function isOrderPack(orderStep: unknown): boolean {
  return normalizeOrderStep(orderStep) > 1
}

export function orderPackCount(qty: number, minOrder: number, orderStep: number): number {
  const min = Math.max(1, Math.trunc(Number(minOrder) || 1))
  const step = normalizeOrderStep(orderStep)
  const n = Math.max(min, Math.trunc(Number(qty) || min))
  if (step <= 1) return n
  return Math.max(1, Math.round((n - min) / step) + 1)
}

export function qtyFromPackCount(
  packs: number,
  minOrder: number,
  orderStep: number,
  maxOrder?: number,
): number {
  const min = Math.max(1, Math.trunc(Number(minOrder) || 1))
  const step = normalizeOrderStep(orderStep)
  const p = Math.max(1, Math.trunc(Number(packs) || 1))
  return snapOrderQty(min + (p - 1) * step, min, step, maxOrder)
}

export function formatOrderPackLabel(orderStep: number, unit: unknown): string {
  const step = normalizeOrderStep(orderStep)
  return `${step.toLocaleString('ko-KR')}${sellUnitNoun(unit)} 묶음`
}

export function formatOrderQtyRule(minOrder: number, orderStep: number, unit: unknown): string {
  const min = Math.max(1, Number(minOrder) || 1)
  const step = normalizeOrderStep(orderStep)
  const noun = sellUnitNoun(unit)
  if (step <= 1) {
    return `최소 ${min.toLocaleString('ko-KR')}${noun}`
  }
  return `최소 ${min.toLocaleString('ko-KR')}${noun} · ${step.toLocaleString('ko-KR')}${noun} 카톤`
}

export function formatOrderStepHint(_minOrder: number, orderStep: number, unit: unknown): string {
  const step = normalizeOrderStep(orderStep)
  const noun = sellUnitNoun(unit)
  if (step <= 1) {
    return `한 카톤의 수량입니다. 1이면 낱개입니다.`
  }
  return `한 카톤 ${step.toLocaleString('ko-KR')}${noun}입니다.`
}

const AUTO_UNIT = /^(1개|개|1박스|박스|1박스\(\d+개\))$/

export function isAutoUnitText(unit: string): boolean {
  const text = String(unit || '').trim()
  return !text || AUTO_UNIT.test(text)
}

export function autoUnitText(policy: Pick<MallSellPolicy, 'sellUnit' | 'packQty'>): string {
  if (normalizeSellUnit(policy.sellUnit) === 'BOX') {
    const pack = normalizePackQty(policy.packQty)
    return pack > 1 ? `1박스(${pack}개)` : '1박스'
  }
  return '1개'
}

export function formatShippingChargeLabel(policy: MallSellPolicy): string {
  const fee = Math.max(0, Number(policy.shippingFee) || 0)
  if (fee <= 0) return '무료'
  const amount = `${fee.toLocaleString('ko-KR')}원`
  const charge = normalizeShippingCharge(policy.shippingCharge)
  const noun = sellUnitNoun(policy.sellUnit)
  if (charge === 'PER_UNIT') return `${noun}당 ${amount}`
  if (charge === 'PER_N') {
    const every = normalizeShippingEvery(policy.shippingEvery)
    return `${every}${noun}마다 ${amount}`
  }
  return amount
}

export interface ShippingLineInput {
  productId?: string
  qty: number
  shippingFee?: number | null
  shippingCharge?: MallShippingCharge | string | null
  shippingEvery?: number | null
}

/** 한 줄 배송비 — 수량 연동 방식만 여기서 곱한다. */
export function calcLineShippingFee(line: ShippingLineInput): number {
  const fee = Math.max(0, Number(line.shippingFee) || 0)
  if (fee <= 0) return 0
  const qty = Math.max(0, Math.trunc(Number(line.qty) || 0))
  const charge = normalizeShippingCharge(line.shippingCharge)
  if (charge === 'PER_UNIT') return fee * qty
  if (charge === 'PER_N') {
    const every = normalizeShippingEvery(line.shippingEvery)
    if (qty <= 0) return 0
    return fee * Math.ceil(qty / every)
  }
  return fee
}

/**
 * 주문 배송비.
 * 주문당(ONCE) 상품은 상품별 1회 후 그중 최댓값(기존).
 * 단위마다·N마다 상품은 줄별로 계산해 더한다.
 */
export function calcOrderShippingFromLines(lines: ShippingLineInput[]): number {
  if (!lines.length) return 0
  const onceByProduct = new Map<string, number>()
  let metered = 0
  let anon = 0

  for (const line of lines) {
    const charge = normalizeShippingCharge(line.shippingCharge)
    const fee = Math.max(0, Number(line.shippingFee) || 0)
    if (charge === 'ONCE') {
      const id = String(line.productId || '').trim() || `__anon_${anon}`
      if (!String(line.productId || '').trim()) anon += 1
      onceByProduct.set(id, Math.max(onceByProduct.get(id) || 0, fee))
    } else {
      metered += calcLineShippingFee(line)
    }
  }

  const onceFees = [...onceByProduct.values()]
  const once = onceFees.length ? Math.max(...onceFees) : 0
  return once + metered
}

export function exampleCheckoutPreview(input: {
  price: number
  minOrder: number
  orderStep?: number
  sellUnit?: unknown
  shippingFee?: number
  shippingCharge?: unknown
  shippingEvery?: unknown
  optionExtra?: number
}): string {
  const noun = sellUnitNoun(input.sellUnit)
  const min = Math.max(1, Number(input.minOrder) || 1)
  const step = normalizeOrderStep(input.orderStep)
  const extra = Math.max(0, Number(input.optionExtra) || 0)
  const unitPrice = Math.max(0, Number(input.price) || 0) + extra
  const goods = unitPrice * min
  const shipping = calcLineShippingFee({
    qty: min,
    shippingFee: input.shippingFee,
    shippingCharge: input.shippingCharge as MallShippingCharge,
    shippingEvery: input.shippingEvery as number,
  })
  const total = goods + shipping
  const extraNote = extra > 0 ? `(옵션 추가금 ${extra.toLocaleString('ko-KR')}원 포함) ` : ''
  const stepNote = step > 1 ? `${step}${noun} 묶음으로 ` : ''
  const shipNote =
    shipping > 0 ? `배송비 ${shipping.toLocaleString('ko-KR')}원` : '배송비 무료'
  return `최소 ${min}${noun} ${stepNote}${extraNote}주문 시 상품 ${goods.toLocaleString('ko-KR')}원 + ${shipNote} = ${total.toLocaleString('ko-KR')}원`
}

/** 환불 배송비 구간 수. 10개당이면 236개는 24구간. */
export function returnShippingBlocks(qty: number, every: number): number {
  const count = Math.max(0, Math.trunc(Number(qty) || 0))
  const block = Math.max(1, Math.trunc(Number(every) || 1))
  if (!count) return 0
  return Math.ceil(count / block)
}

/** N개당 X원. 기준 수량 이하는 1회, 넘치면 올림. */
export function returnShippingFee(qty: number, every: number, fee: number): number {
  const unit = Math.max(0, Math.trunc(Number(fee) || 0))
  return returnShippingBlocks(qty, every) * unit
}
